import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, X, Clock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { InlineLoading } from "@/components/common/Loading";
import { Money } from "@/components/common/Money";
import { useCoupon } from "@/hooks/useCoupons";
import { useMatchesByIds } from "@/hooks/useMatches";
import { formatDate, formatTime } from "@/lib/format";
import type { BetSelection, Match } from "@/types/domain";

interface CouponBetPayload {
  matchId: number;
  bet: BetSelection;
  result?: "correct" | "wrong" | "pending" | "refunded";
}

export function CouponDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data: coupon, isLoading } = useCoupon(id);

  // `bets` en base ne stocke que { matchId, bet, result? } — les infos du
  // match (équipes, ligue, score live) sont récupérées à part et fusionnées
  // ici, sinon la page affiche juste "? vs ?" pour chaque ligne.
  const rawBets = (coupon?.bets as unknown as CouponBetPayload[]) ?? [];
  const matchIds = rawBets.map((b) => b.matchId);
  const { data: matches, isLoading: matchesLoading } = useMatchesByIds(matchIds);

  if (isLoading) return <InlineLoading />;
  if (!coupon) {
    return (
      <div className="py-12 text-center">
        <p>{t("common.error")}</p>
        <Button asChild variant="link"><Link to="/">{t("common.back")}</Link></Button>
      </div>
    );
  }

  const matchById = new Map((matches ?? []).map((m) => [m.id, m]));
  const bets = rawBets.map((b) => ({ ...b, match: matchById.get(b.matchId) }));

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to="/"><ArrowLeft /></Link>
        </Button>
        <div className="flex-1">
          <h1 className="font-display text-xl font-bold">{coupon.type}</h1>
          <p className="text-xs text-muted-foreground">{formatDate(coupon.created_at)}</p>
        </div>
        <StatusBadge status={coupon.status} />
      </header>

      <Card>
        <CardContent className="py-4 grid grid-cols-3 gap-3 text-center">
          <Stat label={t("coupon.correct")} value={coupon.correct_count} tone="success" />
          <Stat label="Total" value={bets.length} />
          <Stat label={t("coupon.potentialWin", { amount: "" }).replace(":", "")} value={<Money amount={coupon.payout_amount} />} tone={coupon.status === "winner" ? "success" : undefined} />
        </CardContent>
      </Card>

      <div className="space-y-2">
        {bets.map((b, idx) => (
          <BetRow key={`${b.matchId}-${idx}`} bet={b} loading={matchesLoading} />
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === "winner" ? "success" : status === "lost" ? "destructive" : status === "refunded" ? "warning" : "secondary";
  const labels: Record<string, string> = { pending: "En cours", winner: "Gagné", lost: "Perdu", refunded: "Remboursé" };
  return <Badge variant={variant as never}>{labels[status] ?? status}</Badge>;
}

function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: "success" | "destructive" }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold ${tone === "success" ? "text-success" : tone === "destructive" ? "text-destructive" : ""}`}>{value}</p>
    </div>
  );
}

const LIVE_STATUSES = ["1H", "HT", "2H", "ET", "P"];
const NO_SCORE_LABELS: Record<string, string> = { CANC: "Annulé", ABD: "Interrompu", PST: "Reporté" };

function BetRow({ bet, loading }: { bet: CouponBetPayload & { match?: Match }; loading: boolean }) {
  const m = bet.match;
  return (
    <Card>
      <CardContent className="py-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground truncate">
            {m?.league.name ?? (loading ? "…" : "—")}
          </p>
          {m && LIVE_STATUSES.includes(m.status) ? (
            <Badge variant="live">{m.elapsed}' · {m.score.home}-{m.score.away}</Badge>
          ) : m && NO_SCORE_LABELS[m.status] ? (
            <Badge variant="destructive">{NO_SCORE_LABELS[m.status]}</Badge>
          ) : m && m.status !== "NS" ? (
            <Badge variant="secondary">{m.score.home}-{m.score.away} {m.status}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">{m ? formatTime(m.kickoff) : loading ? "…" : "—"}</span>
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {m ? `${m.home.name} vs ${m.away.name}` : loading ? "Chargement…" : "Match introuvable"}
            </p>
            <p className="text-xs text-muted-foreground">{betShort(bet.bet)}</p>
          </div>
          <ResultIcon r={bet.result} />
        </div>
      </CardContent>
    </Card>
  );
}

function ResultIcon({ r }: { r?: string }) {
  if (r === "correct") return <span className="flex size-8 items-center justify-center rounded-full bg-success/15 text-success"><Check className="size-4" /></span>;
  if (r === "wrong") return <span className="flex size-8 items-center justify-center rounded-full bg-destructive/15 text-destructive"><X className="size-4" /></span>;
  if (r === "refunded") return <span className="flex size-8 items-center justify-center rounded-full bg-warning/15 text-warning"><RefreshCw className="size-4" /></span>;
  return <span className="flex size-8 items-center justify-center rounded-full bg-muted text-muted-foreground"><Clock className="size-4" /></span>;
}

function betShort(b: BetSelection): string {
  const ou = (line: number, pick: "over" | "under") => `${pick === "over" ? "+" : "-"}${line}`;
  switch (b.type) {
    case "result":
      return `1X2 → ${b.pick}`;
    case "doubleChance":
      return `Double chance → ${b.pick}`;
    case "dnb":
      return `DNB → ${b.pick}`;
    case "htResult":
      return `Mi-temps 1X2 → ${b.pick}`;
    case "htft":
      return `Mi-temps/Fin → ${b.ht}/${b.ft}`;
    case "score":
      return `Score → ${b.home}-${b.away}`;
    case "goals":
      return `${ou(b.line, b.pick)} buts`;
    case "btts":
      return `BTTS → ${b.pick === "yes" ? "Oui" : "Non"}`;
    case "htGoals":
      return `Buts mi-temps ${ou(b.line, b.pick)}`;
    case "teamGoals":
      return `Buts ${b.team === "home" ? "dom" : "ext"} ${ou(b.line, b.pick)}`;
    case "corners":
      return `Corners ${ou(b.line, b.pick)}`;
    case "cards":
      return `Cartons ${ou(b.line, b.pick)}`;
    case "shots":
      return `Tirs ${ou(b.line, b.pick)}`;
    case "fouls":
      return `Fautes ${ou(b.line, b.pick)}`;
  }
}
